import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronRight, User, LogOut, Mail, Shield, Menu, Home, ClipboardList, Bell, CheckCheck } from 'lucide-react';
import { useContactNotifications } from '../../hooks/useContactNotifications';
import { NotificationsService, type Notificacion } from '../../services/notifications/notifications.service';
import { usePermissions } from '../../hooks/usePermissions';
import { useSurveyStatus } from '../../hooks/useSurveyStatus';
import { useAuthStore } from '../../store/auth.store';

interface Crumb {
  label: string;
  to?: string;
}

const SECTIONS: Record<string, { label: string; pages: Record<string, string> }> = {
  miembros: { label: 'Miembros', pages: { usuarios: 'Usuarios', membresias: 'Membresías' } },
  seguridad: { label: 'Seguridad', pages: { roles: 'Roles', modulos: 'Módulos', permisos: 'Permisos' } },
  iglesia: {
    label: 'Iglesia',
    pages: {
      ministerios: 'Ministerios',
      contactos: 'Contactos',
      'programacion-semanal': 'Programación Semanal',
      chat: 'Mensajes',
    },
  },
  config: { label: 'Configuración', pages: { 'contenido-publico': 'Contenido Público' } },
};

const SINGLE_PAGES: Record<string, string> = {
  eventos: 'Eventos',
  miembro: 'Mi Perfil',
  mayordomia: 'Mayordomía',
  donaciones: 'Donaciones',
  fundacion: 'Fundación',
  reportes: 'Reportes',
};

const getBreadcrumbs = (pathname: string): Crumb[] => {
  const relative = pathname.replace('/dashboard', '').replace(/\/+$/, '');
  const crumbs: Crumb[] = [{ label: 'Dashboard', to: '/dashboard' }];
  if (!relative) return crumbs;

  const [segment, page] = relative.split('/').filter(Boolean);
  const section = SECTIONS[segment];
  if (section) {
    crumbs.push({ label: section.label });
    if (page && section.pages[page]) crumbs.push({ label: section.pages[page] });
  } else if (SINGLE_PAGES[segment]) {
    crumbs.push({ label: SINGLE_PAGES[segment] });
  }
  return crumbs;
};

interface Usuario {
  id: string;
  email: string;
  nombreCompleto: string;
  tipoUsuario: string;
  estado: boolean;
}

interface DashboardHeaderProps {
  usuario?: Usuario;
  onOpenProfile?: (config?: { tab?: 'perfil' | 'adicionales'; editMode?: boolean }) => void;
  onToggleMobileMenu?: () => void;
  isCollapsed?: boolean;
}

export const DashboardHeader: React.FC<DashboardHeaderProps> = ({ usuario, onOpenProfile, onToggleMobileMenu, isCollapsed = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const crumbs = getBreadcrumbs(location.pathname);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);
  const { totalNuevos } = useContactNotifications();

  // Notificaciones generales (eventos, recordatorios, sistema)
  const generalNotifRef = useRef<HTMLDivElement>(null);
  const [showGeneralNotifs, setShowGeneralNotifs] = useState(false);
  const [generalNotifs, setGeneralNotifs] = useState<Notificacion[]>([]);
  const [noLeidas, setNoLeidas] = useState(0);

  const cargarNoLeidas = async () => {
    try {
      const count = await NotificationsService.contarNoLeidas();
      setNoLeidas(count);
    } catch { /* ignore */ }
  };

  const cargarNotificaciones = async () => {
    try {
      const data = await NotificationsService.listarMine(20);
      setGeneralNotifs(data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    cargarNoLeidas();
    const interval = setInterval(cargarNoLeidas, 60000);
    return () => clearInterval(interval);
  }, []);

  const toggleGeneralNotifs = () => {
    const next = !showGeneralNotifs;
    setShowGeneralNotifs(next);
    if (next) cargarNotificaciones();
  };

  const handleMarcarTodas = async () => {
    try {
      await NotificationsService.marcarTodasLeidas();
      setNoLeidas(0);
      await cargarNotificaciones();
    } catch { /* ignore */ }
  };
  const { hasPermission } = usePermissions();
  const { user: authUser } = useAuthStore();
  const activeRole =
    authUser?.roles?.find((r: any) => r.activo)?.nombre || authUser?.roles?.[0]?.nombre;
  const { status: surveyStatus, loading: surveyLoading } = useSurveyStatus();
  const surveyCompletitud = surveyStatus?.completitud ?? 0;
  const surveyPendiente = !surveyLoading && surveyStatus !== null && surveyCompletitud < 100;

  // Cerrar menús al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
      if (generalNotifRef.current && !generalNotifRef.current.contains(event.target as Node)) {
        setShowGeneralNotifs(false);
      }
    };

    if (showUserMenu || showNotifications || showGeneralNotifs) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu, showNotifications, showGeneralNotifs]);

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userData');
    navigate('/login');
  };

  return (
    <header
      className={`fixed top-0 right-0 z-50 h-14 lg:h-16 bg-gradient-to-r from-white via-gray-50 to-white shadow-[0_1px_3px_rgba(0,0,0,0.07)] border-b border-gray-200/60 backdrop-blur-sm transition-[left] duration-200 ease-in-out left-0 ${
        isCollapsed ? 'lg:left-14' : 'lg:left-56'
      }`}
    >
      <div className="h-full px-3 lg:px-6 flex justify-between items-center">
        {/* Botón hamburguesa móvil */}
        <button
          onClick={onToggleMobileMenu}
          className="lg:hidden p-2 -ml-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
          aria-label="Abrir menú"
        >
          <Menu className="w-6 h-6" />
        </button>

        {/* Breadcrumb */}
        <nav aria-label="Ruta de navegación" className="flex items-center min-w-0">
          <ol className="flex items-center gap-1 text-xs min-w-0">
            {crumbs.map((crumb, index) => {
              const isLast = index === crumbs.length - 1;
              return (
                <li key={index} className="flex items-center gap-1 min-w-0">
                  {index > 0 && <ChevronRight className="w-3.5 h-3.5 text-gray-300 shrink-0" />}
                  {crumb.to && !isLast ? (
                    <button
                      onClick={() => navigate(crumb.to!)}
                      className="flex items-center gap-1 text-gray-500 hover:text-purple-600 transition-colors truncate"
                    >
                      {index === 0 && <Home className="w-3.5 h-3.5 shrink-0" />}
                      <span className="truncate">{crumb.label}</span>
                    </button>
                  ) : (
                    <span
                      className={`flex items-center gap-1 truncate ${
                        isLast ? 'font-medium text-gray-900' : 'text-gray-500'
                      }`}
                    >
                      {index === 0 && <Home className="w-3.5 h-3.5 shrink-0 text-gray-400" />}
                      <span className="truncate">{crumb.label}</span>
                    </span>
                  )}
                </li>
              );
            })}
          </ol>
        </nav>

        {/* Información del usuario y acciones */}
        <div className="flex items-center space-x-3 lg:space-x-6">
          {/* Notificaciones generales — visible para todos los usuarios autenticados */}
          <div className="relative" ref={generalNotifRef}>
            <button
              onClick={toggleGeneralNotifs}
              className="relative p-2 text-gray-400 hover:text-gray-600 transition-colors duration-200 group"
            >
              <Bell className="w-5 h-5 lg:w-6 lg:h-6" />
              {noLeidas > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center bg-purple-500 text-white text-[10px] font-medium rounded-full ring-2 ring-white px-1">
                  {noLeidas > 99 ? '99+' : noLeidas}
                </span>
              )}
            </button>

            {/* Dropdown de notificaciones generales */}
            <div className={`absolute right-0 mt-3 w-80 sm:w-96 bg-white rounded-2xl shadow-xl border border-gray-200/60 z-50 backdrop-blur-sm transform transition-all duration-300 ease-in-out origin-top-right ${
              showGeneralNotifs
                ? 'opacity-100 scale-100 translate-y-0'
                : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'
            }`}>
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <div>
                  <h3 className="text-xs font-medium text-gray-900">Notificaciones</h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {noLeidas > 0 ? `${noLeidas} sin leer` : 'Estás al día'}
                  </p>
                </div>
                {noLeidas > 0 && (
                  <button
                    onClick={handleMarcarTodas}
                    className="flex items-center gap-1 text-[11px] text-purple-600 hover:text-purple-800 font-medium"
                  >
                    <CheckCheck className="w-3.5 h-3.5" />
                    Marcar todas leídas
                  </button>
                )}
              </div>
              <div className="max-h-80 overflow-y-auto">
                {generalNotifs.length === 0 ? (
                  <p className="px-4 py-6 text-center text-xs text-gray-400">No tienes notificaciones.</p>
                ) : (
                  generalNotifs.map((n) => (
                    <div
                      key={n.id}
                      onClick={async () => {
                        if (!n.leida) {
                          try {
                            await NotificationsService.marcarLeida(n.id);
                            setGeneralNotifs((prev) => prev.map((x) => (x.id === n.id ? { ...x, leida: true } : x)));
                            setNoLeidas((prev) => Math.max(0, prev - 1));
                          } catch { /* ignore */ }
                        }
                      }}
                      className={`px-4 py-3 border-b border-gray-50 cursor-pointer transition-colors ${
                        n.leida ? 'opacity-60 hover:bg-gray-50' : 'bg-purple-50/40 hover:bg-purple-50'
                      }`}
                    >
                      <div className="flex items-start gap-2.5">
                        <span className={`mt-1 w-2 h-2 rounded-full shrink-0 ${n.leida ? 'bg-gray-200' : 'bg-purple-500'}`} />
                        <div className="min-w-0">
                          <p className={`text-xs ${n.leida ? 'text-gray-600' : 'font-medium text-gray-900'}`}>{n.titulo}</p>
                          <p className="text-[11px] text-gray-500 mt-0.5 line-clamp-2">{n.mensaje}</p>
                          <p className="text-[10px] text-gray-400 mt-1">
                            {new Date(n.creadoEn).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' })}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Notificaciones de contacto — solo visible si tiene permiso 'ver' en Contactos */}
          {hasPermission('Contactos', 'ver') && (
            <div className="relative" ref={notificationsRef}>
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2 text-gray-400 hover:text-gray-600 transition-colors duration-200 group"
              >
                <Mail className="w-5 h-5 lg:w-6 lg:h-6" />
                {totalNuevos > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[10px] font-medium rounded-full ring-2 ring-white px-1">
                    {totalNuevos > 99 ? '99+' : totalNuevos}
                  </span>
                )}
              </button>

              {/* Dropdown de notificaciones */}
              <div className={`absolute right-0 mt-3 w-72 sm:w-80 bg-white rounded-2xl shadow-xl border border-gray-200/60 py-4 z-50 backdrop-blur-sm transform transition-all duration-300 ease-in-out origin-top-right ${
                showNotifications
                  ? 'opacity-100 scale-100 translate-y-0'
                  : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'
              }`}>
                <div className="px-4 pb-3 border-b border-gray-100">
                  <h3 className="text-xs font-medium text-gray-900">Mensajes de Contacto</h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {totalNuevos > 0 ? `${totalNuevos} nuevo${totalNuevos !== 1 ? 's' : ''}` : 'Sin mensajes nuevos'}
                  </p>
                </div>
                <div className="py-2">
                  <button
                    onClick={() => {
                      setShowNotifications(false);
                      navigate('/dashboard/iglesia/contactos');
                    }}
                    className="w-full px-4 py-3 text-left hover:bg-purple-50/60 transition-colors duration-200 flex items-center space-x-3 text-gray-700 hover:text-purple-600"
                  >
                    <Mail className="w-5 h-5" />
                    <span className="text-xs font-medium">Ver todos los mensajes</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Información del usuario con dropdown */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center space-x-2 lg:space-x-3 p-1.5 lg:p-2 rounded-xl hover:bg-purple-50/60 transition-all duration-200 group"
            >
              <div className="hidden lg:block text-right">
                <p className="text-xs font-medium text-gray-700 group-hover:text-purple-600 transition-colors">
                  {usuario?.nombreCompleto || 'Usuario'}
                </p>
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] font-medium text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded border border-purple-100">
                    {activeRole || usuario?.tipoUsuario || 'Invitado'}
                  </span>
                </div>
              </div>
              
              <div className="relative">
                <div className="w-8 h-8 lg:w-10 lg:h-10 bg-gradient-to-br from-purple-500/80 to-blue-500/80 rounded-xl flex items-center justify-center shadow-md group-hover:shadow-lg transition-all duration-200">
                  <span className="text-white text-xs font-medium">
                    {usuario?.nombreCompleto?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
                <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 lg:w-4 lg:h-4 bg-green-400 rounded-full ring-2 ring-white"></div>
              </div>
              
              <ChevronDown className={`hidden sm:block w-4 h-4 text-gray-400 group-hover:text-purple-500/80 transition-all duration-300 ${
                showUserMenu ? 'rotate-180' : ''
              }`} />
            </button>

            {/* Dropdown Menu */}
            <div className={`absolute right-0 mt-4 w-[calc(100vw-1.5rem)] max-w-sm sm:max-w-md lg:w-96 bg-white rounded-2xl shadow-xl border border-gray-200/60 py-6 z-50 backdrop-blur-sm transform transition-all duration-300 ease-in-out origin-top-right ${
              showUserMenu 
                ? 'opacity-100 scale-100 translate-y-0' 
                : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'
            }`}>
              {/* User Info Section */}
              <div className="px-4 py-3 border-b border-gray-100">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500/80 to-blue-500/80 rounded-xl flex items-center justify-center shadow-lg">
                    <span className="text-white font-medium">
                      {usuario?.nombreCompleto?.charAt(0).toUpperCase() || 'U'}
                    </span>
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-700">
                      {usuario?.nombreCompleto || 'Usuario'}
                    </p>
                    <p className="text-xs text-gray-600">{usuario?.email}</p>
                    <div className="flex items-center gap-1.5 mt-1">
                      <span className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider text-purple-700 bg-purple-50 px-1.5 py-0.5 rounded border border-purple-100">
                        <Shield className="w-3 h-3" />
                        {activeRole || usuario?.tipoUsuario || 'Invitado'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Alerta: perfil incompleto */}
              {surveyPendiente && (
                <div className="mx-4 mb-2 rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 p-3">
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="w-6 h-6 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center text-white shrink-0">
                      <ClipboardList className="w-3.5 h-3.5" />
                    </div>
                    <p className="text-xs font-medium text-gray-800">Completa tu perfil</p>
                    <span className="ml-auto text-[10px] font-medium text-orange-600 bg-orange-100 px-1.5 py-0.5 rounded-full">
                      {surveyCompletitud}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5 mb-2">
                    <div
                      className="bg-gradient-to-r from-amber-500 to-orange-500 h-1.5 rounded-full transition-all duration-500"
                      style={{ width: `${surveyCompletitud}%` }}
                    />
                  </div>
                  <p className="text-[11px] text-amber-700 mb-2">
                    {surveyStatus?.camposFaltantes?.length || 0} campos pendientes
                  </p>
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      onOpenProfile?.({ tab: 'adicionales', editMode: true });
                    }}
                    className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-amber-500 to-orange-500 rounded-lg hover:from-amber-600 hover:to-orange-600 transition-all shadow-sm"
                  >
                    Completar encuesta
                  </button>
                </div>
              )}

              {/* Menu Items */}
              <div className="py-2">
                <button 
                  onClick={() => {
                    setShowUserMenu(false);
                    onOpenProfile?.();
                  }}
                  className="w-full px-4 py-3 text-left hover:bg-purple-50/60 transition-colors duration-200 flex items-center space-x-3 text-gray-700 hover:text-purple-600"
                >
                  <User className="w-5 h-5" />
                  <span className="text-xs font-medium">Mi Perfil</span>
                </button>
                
                <div className="h-px bg-gray-100 my-2 mx-4"></div>
                
                <button
                  onClick={handleLogout}
                  className="w-full px-4 py-3 text-left hover:bg-red-50 transition-colors duration-200 flex items-center space-x-3 text-red-600 hover:text-red-700"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="text-xs font-medium">Cerrar Sesión</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};